import { DateTime } from "luxon";
import callCloudRunFunction from "../cloudRunServices";
import { safeLocalStorage } from "../../utils/safeStorage";
import { getCoordinatedActivitySession } from "../session/activitySessionIdentity";

const safeParse = (value) => {
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const getOnceMap = (storageKey = "") => {
  try {
    const raw = safeLocalStorage.getItem(storageKey);
    const parsed = raw ? safeParse(raw) : null;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (error) {
    return {};
  }
};

const setOnceMap = (storageKey = "", value = {}) => {
  try {
    safeLocalStorage.setItem(storageKey, JSON.stringify(value));
  } catch (error) {
    // Storage failures should not block telemetry writes.
  }
};

const pruneOnceMapIfNeeded = (mapObj = {}, maxEntries = 1500) => {
  const keys = Object.keys(mapObj || {});
  if (keys.length <= maxEntries) return mapObj;

  keys.sort((left, right) => (mapObj[left] || 0) - (mapObj[right] || 0));
  const next = { ...mapObj };
  const deleteCount = keys.length - maxEntries;
  for (let index = 0; index < deleteCount; index += 1) {
    delete next[keys[index]];
  }
  return next;
};

const toBase64Url = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let value = "";
  for (let index = 0; index < bytes.length; index += 1) {
    value += String.fromCharCode(bytes[index]);
  }
  return btoa(value)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
};

const sha256Base64Url = async (input) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  const hash = await crypto.subtle.digest("SHA-256", data);
  return toBase64Url(hash);
};

const dayKeyLA = () =>
  DateTime.now().setZone("America/Los_Angeles").toISODate();

const generateClientEventId = () => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}_${Math.random().toString(36).slice(2)}`;
};

const sendExperimentTelemetryEvent = (event) =>
  callCloudRunFunction(
    "funnel-telemetry-handler",
    "trackExperimentTelemetry",
    { events: [event] },
    {
      requiresAuth: false,
      includeAuthToken: true,
      includeAppCheckToken: true,
      retry: { retries: 1, backoffMs: 500 },
    },
  );

const responseAcceptedEvent = (response = {}) =>
  Number(response?.acceptedEvents || 0) > 0 &&
  !(
    Array.isArray(response?.rejectedEvents) &&
    response.rejectedEvents.length > 0
  );

const OUTBOX_MAX_ENTRIES = 100;
const OUTBOX_MAX_AGE_MS = 35 * 24 * 60 * 60 * 1000;
const OUTBOX_FLUSH_CONCURRENCY = 4;

const isCriticalExperimentFact = (payload = {}) =>
  ["assignment", "exposure"].includes(payload?.properties?.metricType) ||
  (payload?.properties?.experimentKey === "member_stamp_card_v1" &&
    payload?.properties?.metricKey === "stamp_card_opened");

const getOutbox = (outboxKey) => {
  const parsed = safeParse(safeLocalStorage.getItem(outboxKey) || "[]");
  const cutoff = Date.now() - OUTBOX_MAX_AGE_MS;
  return (Array.isArray(parsed) ? parsed : [])
    .filter(
      (row) => row?.key && row?.payload && Number(row.queuedAt || 0) >= cutoff,
    )
    .slice(-OUTBOX_MAX_ENTRIES);
};

const setOutbox = (outboxKey, rows) => {
  try {
    safeLocalStorage.setItem(
      outboxKey,
      JSON.stringify((rows || []).slice(-OUTBOX_MAX_ENTRIES)),
    );
  } catch (_error) {
    // The safe storage wrapper retains an in-memory fallback.
  }
};

export const buildExperimentIdempotencyKey = ({
  scope = "session",
  event,
  userId = null,
  sessionId = null,
  key = null,
}) => {
  const subject =
    scope === "user"
      ? userId || "anon"
      : scope === "day"
        ? userId || sessionId || "anon"
        : sessionId || userId || "anon";

  const day = scope === "day" ? dayKeyLA() : "";
  const extra = key ? String(key) : "";

  return [scope, event, subject, day, extra].filter(Boolean).join("|");
};

export const createExperimentEventStore = ({
  storageKey = "",
  maxOnceEntries = 1500,
  isDisabled = () => false,
} = {}) => {
  const pendingOnceKeys = new Set();
  const outboxKey = `${storageKey || "experimentEvents"}:criticalOutbox:v1`;
  let outboxFlushPromise = null;

  const persistCriticalPayload = (payload) => {
    if (!isCriticalExperimentFact(payload)) return;
    const key = payload.idempotencyKey || payload.clientEventId;
    const rows = getOutbox(outboxKey).filter((row) => row.key !== key);
    rows.push({ key, queuedAt: Date.now(), payload });
    setOutbox(outboxKey, rows);
  };

  const removeOutboxPayload = (payload) => {
    const key = payload.idempotencyKey || payload.clientEventId;
    if (!key) return;
    setOutbox(
      outboxKey,
      getOutbox(outboxKey).filter((row) => row.key !== key),
    );
  };

  const sendPayload = async (payload) => {
    const response = await sendExperimentTelemetryEvent(payload);
    if (responseAcceptedEvent(response)) {
      removeOutboxPayload(payload);
      return true;
    }
    return false;
  };

  const flushCriticalOutbox = () => {
    if (outboxFlushPromise) return outboxFlushPromise;
    outboxFlushPromise = (async () => {
      if (isDisabled()) return { attempted: 0, accepted: 0, pending: 0 };
      const rows = getOutbox(outboxKey);
      setOutbox(outboxKey, rows);
      let accepted = 0;
      const phases = [
        rows.filter(
          (row) => row.payload?.properties?.metricType === "assignment",
        ),
        rows.filter(
          (row) => row.payload?.properties?.metricType !== "assignment",
        ),
      ];
      for (const phaseRows of phases) {
        let cursor = 0;
        const workers = Array.from(
          {
            length: Math.min(phaseRows.length, OUTBOX_FLUSH_CONCURRENCY),
          },
          async () => {
            while (cursor < phaseRows.length) {
              const row = phaseRows[cursor];
              cursor += 1;
              try {
                if (await sendPayload(row.payload)) accepted += 1;
              } catch (_error) {
                // Keep the item for the next bounded retry.
              }
            }
          },
        );
        await Promise.all(workers);
      }
      return {
        attempted: rows.length,
        accepted,
        pending: getOutbox(outboxKey).length,
      };
    })().finally(() => {
      outboxFlushPromise = null;
    });
    return outboxFlushPromise;
  };

  if (typeof window !== "undefined" && window.addEventListener) {
    window.addEventListener("online", flushCriticalOutbox);
  }
  if (getOutbox(outboxKey).length && typeof queueMicrotask === "function") {
    queueMicrotask(() => {
      void flushCriticalOutbox();
    });
  }

  const trackIdempotent = async ({
    event,
    userId = null,
    sessionId = null,
    properties = {},
    context = {},
    idempotencyKey,
  }) => {
    if (!event || !idempotencyKey) return null;
    if (isDisabled()) return null;

    try {
      const occurredAtClient = new Date().toISOString();
      const activitySession = await getCoordinatedActivitySession({
        occurredAtMs: Date.parse(occurredAtClient),
        qualifyingActivity: true,
        activitySessionOwnerId: sessionId || userId || "",
      });
      const payload = {
        event,
        userId: userId || null,
        sessionId: sessionId || null,
        step:
          properties?.step != null && properties?.step !== ""
            ? properties.step
            : properties?.view != null && properties?.view !== ""
              ? properties.view
              : null,
        properties: properties || {},
        context: context || {},
        idempotencyKey,
        activitySessionId: activitySession.activitySessionId,
        occurredAtClient,
      };
      persistCriticalPayload(payload);
      const accepted = await sendPayload(payload);
      if (!accepted) return null;
      if (properties?.metricType === "assignment") {
        await flushCriticalOutbox();
      }
      return sha256Base64Url(idempotencyKey);
    } catch (error) {
      const message = String(error?.message || "");
      const code = error?.code || "";
      if (
        code === "permission-denied" ||
        message.toLowerCase().includes("permission")
      ) {
        return null;
      }
      console.error("experimentEvents trackIdempotent failed:", error);
      return null;
    }
  };

  const trackEvent = async ({
    event,
    userId = null,
    sessionId = null,
    properties = {},
    context = {},
  }) => {
    if (!event) return null;
    if (isDisabled()) return null;

    try {
      const clientEventId = generateClientEventId();
      const occurredAtClient = new Date().toISOString();
      const activitySession = await getCoordinatedActivitySession({
        occurredAtMs: Date.parse(occurredAtClient),
        qualifyingActivity: true,
        activitySessionOwnerId: sessionId || userId || "",
      });
      const payload = {
        event,
        userId: userId || null,
        sessionId: sessionId || null,
        step:
          properties?.step != null && properties?.step !== ""
            ? properties.step
            : properties?.view != null && properties?.view !== ""
              ? properties.view
              : null,
        properties: properties || {},
        context: context || {},
        clientEventId,
        activitySessionId: activitySession.activitySessionId,
        occurredAtClient,
      };
      persistCriticalPayload(payload);
      return (await sendPayload(payload)) ? clientEventId : null;
    } catch (error) {
      console.error("experimentEvents trackEvent failed:", error);
      return null;
    }
  };

  const trackOnce = async ({
    event,
    userId = null,
    sessionId = null,
    scope = "session",
    key = null,
    properties = {},
    context = {},
  }) => {
    if (!event) return null;

    const idempotencyKey = buildExperimentIdempotencyKey({
      scope,
      event,
      userId,
      sessionId,
      key,
    });

    if (storageKey) {
      const onceMap = getOnceMap(storageKey);
      if (onceMap[idempotencyKey]) {
        return null;
      }
      if (pendingOnceKeys.has(idempotencyKey)) return null;
      pendingOnceKeys.add(idempotencyKey);
    }

    const eventId = await trackIdempotent({
      event,
      userId,
      sessionId,
      properties,
      context,
      idempotencyKey,
    });

    if (storageKey) {
      pendingOnceKeys.delete(idempotencyKey);
      if (eventId) {
        const onceMap = getOnceMap(storageKey);
        onceMap[idempotencyKey] = Date.now();
        setOnceMap(storageKey, pruneOnceMapIfNeeded(onceMap, maxOnceEntries));
      }
    }

    return eventId;
  };

  return {
    trackEvent,
    trackIdempotent,
    trackOnce,
    flushCriticalOutbox,
  };
};
